import type { UseQueryResult } from "react-query";
import { useQueries, useQuery } from "react-query";
import config from "../../config";
import { getAsync } from "../protect-api";
import { getUserIdFromToken } from "../../utils/encrypt-decrypt-utilities";
import type {
  ServiceDetails,
  ServiceLinePayloadAction,
} from "../../store/UserSlice";
import { apiVersion } from "../../shared/constants/common.constant";
import { getAccessTokenFromStorage } from "../../utils/auth-session/access-token";

export const USER_KEY = "user";
export const USER_PROFILE = "user_profile";
export const USER_SEARCHABLE_COUNTRIES = "user_searchable_countries";
export const CHECK_SERVICE_LINE = "check_service_line";
export const FEATURE_FLAGS = "feature_flags";
export const USER_PERMISSIONS = "user_permissions";

const uppApiVersion = "v1";

// UPP test env API to get user details for upp-search. Calling only when running Protect independently
const getUserProfile = async (): Promise<object> => {
  const { data } = await getAsync(
    `${config.UPP_SEARCH_API_URL}/${uppApiVersion}/users/${getUserIdFromToken(
      getAccessTokenFromStorage() ?? ""
    )}`,
    undefined,
    undefined,
    true
  );
  return data;
};

// UPP test env API to get user details for upp-search. Calling only when running Protect independently
export const useGetUserProfile = (): UseQueryResult<object> =>
  useQuery<object>([USER_PROFILE], () => getUserProfile(), {
    keepPreviousData: true,
  });

// UPP test env API to get user details for upp-search. Calling only when running Protect independently
const getUserSearchableCountries = async (countryCode: string): Promise<[]> => {
  const { data } = await getAsync(
    `${config.UPP_SEARCH_API_URL}/ubo/Reference/mapinfo/${countryCode}`,
    undefined,
    undefined,
    true
  );
  return data;
};

// UPP test env API to get user details for upp-search. Calling only when running Protect independently
export const useGetUserSearchableCountries = (
  countryCode: string
): UseQueryResult<[]> =>
  useQuery<[]>(
    [USER_SEARCHABLE_COUNTRIES, countryCode],
    () => getUserSearchableCountries(countryCode),
    {
      keepPreviousData: true,
    }
  );

export const getUserServiceLine = async (
  serviceId: number
): Promise<object> => {
  const userId = getUserIdFromToken(getAccessTokenFromStorage() ?? "");
  const { data } = await getAsync(
    `${config.SERVICE_LINE_CHECK_API_URL}/users/${userId}/services/${serviceId}/check?amount=0`,
    undefined,
    undefined,
    true
  );
  return data;
};

export const useCheckUserServiceLines = (
  kycServiceLinesState: ServiceDetails[]
): UseQueryResult<ServiceLinePayloadAction>[] =>
  useQueries(
    kycServiceLinesState.map((service: ServiceDetails) => ({
      queryKey: [CHECK_SERVICE_LINE, service.serviceId],
      queryFn: (): Promise<ServiceLinePayloadAction> =>
        getUserServiceLine(service.serviceId).then((res) => {
          return { ...res, service } as ServiceLinePayloadAction;
        }),
    }))
  );

export const getFeatureFlags = async (featureName: string): Promise<[]> => {
  const { data } = await getAsync<[]>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/Features/${featureName}`,
    undefined,
    undefined,
    true
  );
  return data;
};

export const useGetFeatureFlags = (featureName: string): UseQueryResult<[]> =>
  useQuery<[]>([FEATURE_FLAGS, featureName], () =>
    getFeatureFlags(featureName)
  );
